import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["onClick"]

import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { formatPrice } from '@/common/function'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { getNameArtFromId } from '@/common/list'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import HistoryChart from '@/components/HistoryChart.vue'
import axios from 'axios'
import { api } from '@/api/http'

interface Additional {
  ptn: number
  qlt: number
}

interface ItemHistory {
  amount: number
  price: number
  time: string
  additional: Additional
}

interface Item {
  itemId: string
  amount: number
  startPrice: number
  buyoutPrice: number
  startTime: string
  endTime: string
  history: ItemHistory[]
  additional: Additional
  averagePrice: number
  priceDifference: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ArtefactPage',
  setup(__props) {

const route = useRoute()

const toast = ref()

const qltMap = {
  0: 'white',
  1: 'green',
  2: 'blue',
  3: 'purple',
  4: 'red',
  5: 'gold'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0]

const tableData = ref([])

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })
}

function calcLiquidity48h(history): number {
  if (!history?.length) return 0

  const now = Date.now()
  const WINDOW = 48 * 60 * 60 * 1000

  const times = history.map(h => new Date(h.time).getTime()).sort((a, b) => a - b)

  // ===== 1. продажи за 48ч =====
  const last48h = times.filter(t => t >= now - WINDOW).length

  // 6 продаж за 48ч = потолок
  const salesScore = Math.min(last48h / 6, 1) * 60

  // ===== 2. средний gap =====
  let avgGap = WINDOW

  if (times.length > 1) {
    let sum = 0
    for (let i = 1; i < times.length; i++) {
      sum += times[i] - times[i - 1]
    }
    avgGap = sum / (times.length - 1)
  }

  // 6ч → отлично, 48ч → 0
  const gapScore = Math.max(0, 1 - avgGap / WINDOW) * 25

  // ===== 3. свежесть =====
  const lastSaleAgo = now - times[times.length - 1]

  // <6ч отлично, >48ч мёртв
  const recencyScore = Math.max(0, 1 - lastSaleAgo / WINDOW) * 15

  return Math.round(Math.min(100, salesScore + gapScore + recencyScore))
}

onMounted(async () => {
  try {
    const { data } = await api.get(`/artefact/${route.params.id}`)

    tableData.value = data.filter((i: any) => !i.isBid)
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      console.error('Ошибка загрузки артефакта:', err.response?.data?.message)
    } else {
      console.error('Ошибка загрузки артефакта:', err)
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Toast), {
      ref_key: "toast",
      ref: toast
    }, null, 512),
    _createElementVNode("h1", null, _toDisplayString(_unref(getNameArtFromId)(_unref(route).params.id)), 1),
    _createVNode(_unref(DataTable), {
      value: tableData.value,
      responsiveLayout: "scroll"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Column), {
          field: "qlt",
          header: "Качество"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("span", {
              style: _normalizeStyle(`color: ${getColorQlt(data.qlt)}`)
            }, _toDisplayString(data.qlt), 5)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "ptn",
          header: "Заточка"
        }),
        _createVNode(_unref(Column), {
          field: "averagePrice",
          header: "Медиана"
        }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(_unref(formatPrice)(data.averagePrice)), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "historyMinPrice",
          header: "Исторический минимум"
        }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(_unref(formatPrice)(data.historyMinPrice)), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "currentPrice",
          header: "Текущий минимум"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("span", {
              style: _normalizeStyle(`color: ${data.averagePrice > data.currentPrice ? 'green' : 'red'}`)
            }, _toDisplayString(_unref(formatPrice)(data.currentPrice)), 5)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "salePrice",
          header: "Цена продажи"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("span", {
              onClick: ($event: any) => (copyToClipboard(data.salePrice))
            }, _toDisplayString(_unref(formatPrice)(Number(data.salePrice))), 9, _hoisted_1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "currentElems",
          header: "Лоты"
        }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(data.currentElems.map(i => i.price)), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), { header: "Ликвидность 48ч" }, {
          body: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(calcLiquidity48h(data.historySalePrices)), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "historySalePrices",
          header: "Количество проданных"
        }, {
          body: _withCtx(({ data }) => [
            _createElementVNode("div", null, _toDisplayString(data.historySalePrices.length), 1),
            _createElementVNode("div", null, [
              _createVNode(HistoryChart, {
                historySalePrices: data.historySalePrices,
                "sale-price": data.salePrice
              }, null, 8, ["historySalePrices", "sale-price"])
            ])
          ]),
          _: 1
        }),
        _createVNode(_unref(Column), {
          field: "profit",
          header: "Профит"
        })
      ]),
      _: 1
    }, 8, ["value"])
  ], 64))
}
}

})